import { TaskResult } from "../contentScript/types";
import { TabProxy } from "./TabProxy";
import { TaskRecorder } from "./TaskRecorder";

/**
 * Callback type for automation phases
 */
export type PhaseCallback = (tab: TabProxy) => Promise<void>;

/**
 * Execute function type - provided by the caller to actually run tasks
 */
export type ExecuteFunction = (tasks: TaskRecorder) => Promise<TaskResult[]>;

/**
 * AutomationRunner enables multi-phase automation execution.
 *
 * Instead of recording all tasks upfront and executing once, this allows
 * automations to execute in phases, using results from earlier phases
 * to inform subsequent phases.
 *
 * @example
 * ```typescript
 * async (runner: AutomationRunner) => {
 *   // Phase 1: Extract a value
 *   const results = await runner.run(async (tab) => {
 *     await tab.getText("#invoice-id");
 *   });
 *
 *   const invoiceId = results.getText();
 *
 *   // Phase 2: Use the extracted value
 *   await runner.run(async (tab) => {
 *     await tab.navigate(`/pay/${invoiceId}`);
 *   });
 * }
 * ```
 */
export class AutomationRunner {
  constructor(private execute: ExecuteFunction) {}

  /**
   * Run a phase of automation tasks and return the results.
   * Each phase gets a fresh TabProxy to record tasks.
   */
  async run(callback: PhaseCallback): Promise<TaskResult[]> {
    const recorder = new TaskRecorder();
    const proxy = new TabProxy(recorder);

    // Let the callback record tasks
    await callback(proxy);

    // Execute the recorded tasks
    return await this.execute(recorder);
  }
}
